import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryGetWarehouses } from "src/queries/warehouses.queries";
import type { WarehouseResource } from "src/services/warehouseService";
import { useSelectDistributor } from "src/store/selectDistributorStore";
import CustomSharedTitleSection from "../../../sipli-fleet-core/components/CustomSharedTitleSection/CustomSharedTitleSection";
import WarehouseResourceGrid from "./components/WarehouseResourceGrid";
import { hierarchyConfig } from "./hierarchyConfig";
import {
  normalizeWarehouseResponse
} from "./warehouseHierarchy.util";


const getOptionalDistributorId = (value: unknown) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) && parsedValue > 0
    ? parsedValue
    : undefined;
};

const Warehouse = () => {
  const navigate = useNavigate();
  const { selectedDistributor } = useSelectDistributor();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] =
    useState<WarehouseResource | null>(null);
  const distributorId = getOptionalDistributorId(selectedDistributor);
  const config = hierarchyConfig.warehouse;

  const params = useMemo(
    () => ({
      sort: "DESC" as const,
      sortBy: "id",
      ...(distributorId ? { idDistributor: distributorId } : {}),
    }),
    [distributorId],
  );

  const query = useQueryGetWarehouses(params);
  const { resources, total } = normalizeWarehouseResponse(
    query.data?.data?.response,
  );

  const closeModal = () => {
    setModalOpen(false);
    setEditingRecord(null);
  };

  return (
    <section className="min-h-full pb-8 pl-2">
      <CustomSharedTitleSection
        titleKey="manage_warehouse"
        buttonKey={config.addKey}
        onClick={() => {
          setEditingRecord(null);
          setModalOpen(true);
        }}
        buttonProps={{ className: "!w-[182px] !h-[40px] !text-gray2" }}
        titleClassName="!text-[25.23px] !font-[700] !leading-[18px"
      />

      <WarehouseResourceGrid
        resource="warehouse"
        resources={resources}
        isLoading={query.isLoading}
        isError={query.isError}
        parentPayload={
          distributorId ? { idDistributor: distributorId } : undefined
        }
        modalOpen={modalOpen}
        editingRecord={editingRecord}
        onModalClose={closeModal}
        onEdit={(record) => {
          setEditingRecord(record);
          setModalOpen(true);
        }}
        onOpen={(record) =>
          navigate(
            `/warehouse-management/area/${encodeURIComponent(
              record.name || String(record.id),
            )}/${record.id}`,
          )
        }
      />
    </section>
  );
};

export default Warehouse;
